/**
 * C안에서 고른 장례식장 마킹.
 *
 * 지역 히어로 폼의 "장례식장 비용" 탭에서 바텀시트로 고른 장례식장 이름을 남긴다.
 * 로그인 화면이 이 값을 읽어 (1) 되짚는 줄에 보여주고 (2) 상담 memo 에 덧붙인다.
 *
 * ## 왜 견적 body 가 아니라 세션인가
 *
 * `QuotationRequestBody`(`types/quotationType.ts`)에는 장례식장을 담을 필드가 없다.
 * 서버 스펙을 늘리면 BE 작업이 붙는데, 이 값이 필요한 곳은 **로그인 화면과 상담 memo**
 * 둘뿐이고 둘 다 프론트에서 조립한다(`LoginContainer` 의 `getFromToMemo`). 그래서
 * 세션에 남기고 로그인에서 읽는 쪽을 택했다.
 *
 * `regionLoginContext`, `matchRequestNeedsContext` 와 같은 패턴이다. 탭 단위라 탭을
 * 닫으면 지워지고, 카카오 왕복은 같은 탭에서 일어나 값이 유지된다.
 */

const PARLOR_PICK_KEY = "region_parlor_pick_context";

/** 한 화면에 늘어놓을 수 있는 한계. 넘으면 되짚는 줄과 memo 가 읽기 어려워진다 */
const MAX_NAMES = 5;

export interface RegionParlorPickContext {
  /** 고른 장례식장 이름(`companyname` 원본). 최대 5곳 */
  names: string[];
}

/**
 * 선택 마킹. **바텀시트에서 고를 때마다** 갱신한다.
 *
 * 제출 시점이 아닌 이유: 이 값은 제출 body 에 실리지 않아 제출을 기다릴 이유가 없고,
 * 고르다 만 사람은 어차피 로그인에 도달하지 않는다. 다음 제출 전에 다시 고르면 덮인다.
 *
 * 빈 배열이면 마킹을 지운다. 탭을 장례식장에서 총비용으로 되돌린 사람에게 옛 선택이
 * 따라붙지 않게 한다.
 */
export const markRegionParlorPick = (names: string[]) => {
  if (typeof window === "undefined") return;
  try {
    if (names.length === 0) {
      sessionStorage.removeItem(PARLOR_PICK_KEY);
      return;
    }
    const context: RegionParlorPickContext = { names: names.slice(0, MAX_NAMES) };
    sessionStorage.setItem(PARLOR_PICK_KEY, JSON.stringify(context));
  } catch {
    // 사파리 프라이빗 등 쓰기 실패. 마킹이 없으면 로그인은 장례식장 줄 없이 흐른다
  }
};

/** 다른 경로로 견적을 낸 경우 마킹을 지운다. 그 경로의 조건이 화면을 결정하게 한다 */
export const clearRegionParlorPick = () => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(PARLOR_PICK_KEY);
  } catch {
    // ignore
  }
};

/**
 * 마킹 읽기. 값이 없거나 형태가 어긋나면 null 을 준다.
 *
 * @returns 고른 장례식장 이름들. 미마킹이거나 파싱 실패면 null
 */
export const getRegionParlorPick = (): RegionParlorPickContext | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(PARLOR_PICK_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.names)) return null;

    const names = parsed.names.filter((n: unknown): n is string => typeof n === "string" && !!n);
    if (names.length === 0) return null;

    return { names: names.slice(0, MAX_NAMES) };
  } catch {
    return null;
  }
};

/**
 * 되짚는 줄 한 칸에 넣을 문구. 이름을 다 늘어놓으면 줄이 넘친다.
 *
 * @example ["해운대백병원장례식장"] -> "해운대백병원장례식장"
 * @example ["A장례식장", "B장례식장"] -> "A장례식장 외 1곳"
 */
export const formatRegionParlorPickLabel = (names: string[]): string => {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  return `${names[0]} 외 ${names.length - 1}곳`;
};
